import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpEventType } from '@angular/common/http';
import { catchError, concatMap, filter, from, of, Subject, takeUntil, tap } from 'rxjs';
import jwt_decode from 'jwt-decode';

import { TranslateConfigService } from '@core/translate-config.service';
import { SystemMessageService } from '@core/services/system-message.service';
import { MediaApi } from '@api/services/media-api';
import { MediaFormat, SelectedFile, UploadStatus } from '@models/media-files';
import { TokenSettings, UploadItem } from './media-upload-external.model';
import { FILE_TYPES } from '@app/constants';
import { SaveMediaRequest } from '@api/index';
import { base64ToBlob } from '@shared/utils/utils';

@Component({
  selector: 'flow-media-upload-external',
  templateUrl: './media-upload-external.component.html',
  styleUrls: ['./media-upload-external.component.scss'],
})
export class MediaUploadExternalComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();
  token: string;
  settings: TokenSettings;
  selectedBody: SelectedFile;
  isDragging = false;
  filesToUpload: UploadItem[] = [];
  allFiles: UploadItem[] = [];
  invalidToken = false;
  availableMediaToUpload: number = 0;
  remainingFilesLimit: number;
  loading = true;
  supportedFileTypes = [...FILE_TYPES.image, ...FILE_TYPES.video];
  uploadingDone: boolean = false;
  disableUpload: boolean = false;
  maxFileSize: number = 0;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;

  constructor(
    private route: ActivatedRoute,
    private systemMessageService: SystemMessageService,
    private mediaApiService: MediaApi,
    private translateConfigService: TranslateConfigService
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.paramMap.get('id');
    this.settings = this.decodeToken(this.token);
    this.maxFileSize = +this.settings?.fileSize * 1024 * 1024;
    this.getMediaTransferAvailableFileCount(this.token);
  }

  private decodeToken(token: string): TokenSettings {
    try {
      return jwt_decode(token);
    } catch (error) {
      this.invalidToken = true;
      return null;
    }
  }

  private getMediaTransferAvailableFileCount(token: string) {
    const body = {
      token: token,
    };

    this.mediaApiService
      .getMediaTransferAvailableFileCount(body)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (remainingFileCount: number) => {
          this.availableMediaToUpload = remainingFileCount;
          this.remainingFilesLimit = this.availableMediaToUpload;
        },
        error: error => {
          console.error('File upload error:', error);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }

  upload(file?) {
    this.disableUpload = true;
    const files = file ? [file] : this.filesToUpload;

    from(files)
      .pipe(
        filter((media: any) => media.status !== UploadStatus.success),
        concatMap(media => {
          return this.processFile(media).pipe(
            filter(event => event.type === HttpEventType.Response),
            tap(() => this.updateStatus(media, UploadStatus.success)),
            catchError(error => {
              this.updateStatus(media, UploadStatus.error, error.message);
              return of(null);
            }),
            takeUntil(this.destroy$)
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        error: error => {
          this.systemMessageService.error(this.translateConfigService.instant(error));
          this.disableUpload = false;
        },
        complete: () => {
          this.disableUpload = false;
          this.filesToUpload = this.filesToUpload.filter(media => media.status !== UploadStatus.success);
          this.remainingFilesLimit =
            this.availableMediaToUpload - this.allFiles.filter(media => media.status === UploadStatus.success).length;

          if (this.remainingFilesLimit === 0 && this.allFiles.every(media => media.status === UploadStatus.success)) {
            this.onUploadComplete();
          }
        },
      });
  }

  processFile(media) {
    const model = this.createRequestModel(media);
    const formData = new FormData();
    media.uploadProgress = null;

    Object.entries(model).forEach(([key, value]) => {
      formData.append(key, value);
    });

    const body = {
      token: this.token,
      file: formData,
    };

    return this.mediaApiService.uploadMediaForExternalUsers(body).pipe(
      tap((event: any) => {
        if (event.type === HttpEventType.UploadProgress) {
          media.uploadProgress = Math.round((event.loaded / (event.total ?? 1)) * 100);
          this.scrollToUploadingItem(media);
        }
      })
    );
  }

  createRequestModel(media): SaveMediaRequest {
    let requestModel: any = {};

    if (media?.selectedFile) {
      requestModel.media = media?.selectedFile;
    }

    if (media?.selectedFile?.videoDuration) {
      requestModel.duration = media?.selectedFile?.videoDuration;
    }

    if (media?.format === MediaFormat.Video && media?.filePreview) {
      const byteCharacters = base64ToBlob(media.filePreview);
      requestModel.videoThumbnail = byteCharacters;
    }

    requestModel.type = media?.format;

    return requestModel;
  }

  updateStatus(media, status: UploadStatus, errorMessage?: string) {
    media.status = status;
    status === UploadStatus.error && (media.errorMessage = errorMessage);
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.scrollContainer?.nativeElement) {
        const element = this.scrollContainer.nativeElement;
        element.scrollTo({
          top: element.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  private scrollToUploadingItem(media: UploadItem): void {
      if (this.scrollContainer?.nativeElement) {
        const container = this.scrollContainer.nativeElement;
        const itemIndex = this.allFiles.indexOf(media);

        if (itemIndex !== -1) {
          const items = container.querySelectorAll('[upload-item-index]');
          const targetItem = items[itemIndex] as HTMLElement;

          if (targetItem) {
            const containerRect = container.getBoundingClientRect();
            const itemRect = targetItem.getBoundingClientRect();

            const itemOffsetTop = itemRect.top - containerRect.top + container.scrollTop;
            const itemHeight = targetItem.offsetHeight;
            const containerHeight = container.clientHeight;

            const targetScrollTop = itemOffsetTop + itemHeight - containerHeight;

            container.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: 'smooth',
            });
          }
        }
      }
  }

  private onUploadComplete(): void {
    this.systemMessageService.success(this.translateConfigService.instant('notifications.success.mediaCreate'));
    this.uploadingDone = true;
    this.disableUpload = false;
  }

  onMaxUploadLimitError() {
    this.maxFilesLimitErrorMsg();
    return;
  }

  selectFile(event): void {
    if (!event) {
      console.warn('No file selected');
      return;
    }

    if (this.filesToUpload.length >= this.remainingFilesLimit) {
      this.maxFilesLimitErrorMsg();
      return;
    }

    this.allFiles.push(event);
    this.filesToUpload.push(event);
    this.scrollToBottom();
  }

  removeFile(i: number) {
    const fileToRemove = this.allFiles[i];

    this.allFiles.splice(i, 1);
    const uploadIndex = this.filesToUpload.indexOf(fileToRemove);

    if (uploadIndex !== -1) {
      this.filesToUpload.splice(uploadIndex, 1);
    }
  }

  maxFilesLimitErrorMsg(): void {
    const errMsg =
      this.remainingFilesLimit === 0 || this.remainingFilesLimit === +this.settings.limitForNumberOfLinks
        ? `The max limit for files is ${+this.settings.limitForNumberOfLinks}`
        : `Cannot upload more than ${this.remainingFilesLimit} files`;

    this.systemMessageService.error(errMsg);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
